import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ShieldCheck, HeartHandshake, Sparkles, Globe, BadgeCheck, Bot, Headset, Users, Briefcase, PiggyBank, GraduationCap, Compass, Landmark, Smartphone, ShoppingBag, Laptop, ShoppingCart, TrainFront, Map, PartyPopper, BadgePercent, UtensilsCrossed, Percent } from "lucide-react";
import { MkCTA } from "@/components/marketing/MarketingLayout";

const fade = (delay = 0) => ({
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.6, delay },
});

const COMPARISONS = [
  {
    icon: Users,
    concern: "Will I ever feel like I belong here?",
    solution: "Join city circles, meetups and members-only events full of students who get it. Friendships here don't depend on freshers' week, they keep growing all year.",
  },
  {
    icon: Briefcase,
    concern: "How do I find a part-time job that actually pays well?",
    solution: "Build a UK-style CV, learn where students successfully apply and access corporate and high-pay part-time opportunities shared by trusted employers and partners.",
  },
  {
    icon: PiggyBank,
    concern: "Why does everything cost so much?",
    solution: "Member deals on SIMs, groceries, electronics, travel and more, plus practical money tips from students who've mastered student budgets in the UK.",
  },
  {
    icon: Bot,
    concern: "Who do I ask when something goes wrong?",
    solution: "Instead of searching dozens of websites, ask Castles AI anytime or request a real support call from our team. University questions, banking, healthcare or everyday confusion: one trusted place to start.",
  },
  {
    icon: Sparkles,
    concern: "How do I make the most of student life?",
    solution: "City guides, weekend trips, members-only events and a community that keeps your calendar full. Student life in the UK, upgraded every single week.",
  },
];

const AI_TOPICS = ["University issues", "Accommodation", "Visa guidance", "Share Code", "GP registration", "Banking", "Scams", "Travel", "Student life", "Everyday confusion"];

const TRUST_BAR = [
  { icon: BadgeCheck, label: "Free Membership Forever" },
  { icon: GraduationCap, label: "Built by Students Who've Lived It" },
  { icon: HeartHandshake, label: "Real Human Support When You Need It" },
  { icon: Compass, label: "One Trusted Place for Guidance, Opportunities & Community" },
];

const CATEGORIES = [
  { icon: Landmark, title: "Bank Accounts", copy: "Student-friendly bank accounts that pay you cashback on every transaction.", link: "/features/save-money", big: true },
  { icon: Briefcase, title: "Corporate & High-Pay Part-Time Jobs", copy: "Find and prepare for high-paying part-time opportunities, not just any job.", link: "/features/jobs", big: true },
  { icon: Smartphone, title: "Student SIM Deals", copy: "SIM and phone deals found for members.", extra: "Get 18 Pro Max, Fold 8 at no-interest instalments today.", link: "/features/save-money", big: true },
  { icon: UtensilsCrossed, title: "50% Discount on Food Brands", copy: "Half-price at food brands students actually love.", link: "/features/student-deals", big: true },
  { icon: ShoppingBag, title: "Shopping Deals", copy: "Fashion, essentials and everything in between.", link: "/features/student-deals" },
  { icon: Laptop, title: "Electronics Deals", copy: "Laptops, headphones and tech for less.", link: "/features/student-deals" },
  { icon: ShoppingCart, title: "Groceries", copy: "Save on your weekly shop, Indian stores included.", link: "/features/save-money" },
  { icon: TrainFront, title: "Student Travel in the UK", copy: "Railcards, coaches and cheap weekend escapes.", link: "/features/weekend-adventures" },
  { icon: Map, title: "City Guides", copy: "Know your city like a local, written by students there.", link: "/features/city-guides" },
  { icon: PartyPopper, title: "Members-Only Events", copy: "Meetups and celebrations across UK cities.", link: "/features/events" },
];

const HERO_BENEFITS = [
  { icon: PiggyBank, label: "Save Every Week", link: "/features/save-money" },
  { icon: BadgePercent, label: "Member Deals", link: "/features/student-deals" },
  { icon: Percent, label: "Discounts", link: "/features/student-deals" },
  { icon: Briefcase, label: "Part-Time Jobs", link: "/features/jobs" },
  { icon: Smartphone, label: "SIM & Phone Deals", link: "/features/save-money" },
  { icon: UtensilsCrossed, label: "50% Off Food Brands", link: "/features/student-deals" },
  { icon: PartyPopper, label: "Student Events", link: "/features/events" },
];

const OUTCOMES = [
  { slug: "community", title: "Meet New People", icon: Users, color: "#10B981", copy: "City circles, meetups and events full of students who feel like your people." },
  { slug: "jobs", title: "Build Your Career", icon: Briefcase, color: "#0A1633", copy: "CV support, job guidance and corporate, high-pay part-time opportunities." },
  { slug: "save-money", title: "Keep More of Your Money", icon: PiggyBank, color: "#D4AF37", copy: "Member deals on the things students actually spend on, week after week." },
];

const STORIES = [
  { img: "https://images.unsplash.com/photo-1621274790572-7c32596bc67f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTZ8MHwxfHNlYXJjaHw0fHx5b3VuZyUyMGluZGlhbiUyMHdvbWFuJTIwdW5pdmVyc2l0eSUyMHN0dWRlbnQlMjBzbWlsaW5nJTIwY2FtcHVzfGVufDB8fHx8MTc4NTk2ODc5Mnww&ixlib=rb-4.1.0&q=85", name: "Simran K.", uni: "University of Leeds", quote: "Two weeks before my flight I already knew which SIM to buy, which bank to open and who was meeting me at arrivals. I landed feeling calm instead of terrified." },
  { img: "https://images.unsplash.com/photo-1724834930670-b7fa43af5c80?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzV8MHwxfHNlYXJjaHwxfHxpbmRpYW4lMjBtYWxlJTIwdW5pdmVyc2l0eSUyMHN0dWRlbnQlMjBiYWNrcGFjayUyMHNtaWxpbmd8ZW58MHx8fHwxNzg1OTY4ODE1fDA&ixlib=rb-4.1.0&q=85", name: "Aditya R.", uni: "University of Leicester", quote: "The money tips saved me a few hundred pounds in my first month. The railcard, the right SIM deal, cooking with flatmates. It all adds up fast." },
  { img: "https://images.unsplash.com/photo-1689580298851-d4482a124290?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MjJ8MHwxfHNlYXJjaHwxfHxpbmRpYW4lMjBmZW1hbGUlMjBjb2xsZWdlJTIwc3R1ZGVudCUyMHBvcnRyYWl0JTIwc21pbGluZyUyMG91dGRvb3JzfGVufDB8fHx8MTc4NTk2OTcwNXww&ixlib=rb-4.1.0&q=75&w=800", name: "Meera J.", uni: "Coventry University", quote: "I found my flatmate through my city circle before I even booked my flight. Moving countries felt a lot less like moving alone." },
];

const TRUST = [
  { icon: BadgeCheck, title: "Built by former international students", copy: "Every guide comes from someone who actually lived it." },
  { icon: HeartHandshake, title: "Real experiences, not theory", copy: "No recycled blog advice. Just what genuinely works." },
  { icon: ShieldCheck, title: "Honest guidance, no commissions", copy: "We recommend what's best for you. Nothing else." },
  { icon: Globe, title: "A growing network across the UK", copy: "New cities, new members, better deals, every month." },
  { icon: Sparkles, title: "Free membership. Always.", copy: "The platform stays free. That's a promise, not a promotion." },
];

const H = ({ children, className = "" }) => (
  <h2 className={`text-3xl sm:text-4xl font-bold tracking-tight text-[#0A1633] ${className}`} style={{ fontFamily: "'Outfit', sans-serif" }}>{children}</h2>
);

export default function Home() {
  const navigate = useNavigate();

  return (
    <main data-testid="home-page">
      {/* HERO */}
      <section className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 pt-12 sm:pt-20 pb-10">
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-14 items-start">
          <div>
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="max-w-xl">
            <p className="text-sm font-semibold tracking-[0.15em] uppercase text-[#D4AF37]" data-testid="hero-overline">Deals · Jobs · Community · Support</p>
            <h1 className="mt-5 text-4xl sm:text-6xl font-bold tracking-tight text-[#0A1633] leading-[1.08]" style={{ fontFamily: "'Outfit', sans-serif" }} data-testid="hero-headline">
              Your Student Success Platform for Life in the UK.
            </h1>
            <div className="mt-6 max-w-lg space-y-2" data-testid="hero-subcopy">
              <p className="text-base sm:text-lg text-slate-600 leading-relaxed">From finding high-paying part-time jobs, SIM and phone deals to learning how to save your money and discovering student events.</p>
              <p className="text-base sm:text-lg text-slate-600 leading-relaxed">Student life made easier, richer and a lot more fun.</p>
              <p className="text-base sm:text-lg font-semibold text-[#0A1633]">One stop for all students.</p>
            </div>
            <div className="mt-9 flex flex-wrap items-center gap-4">
              <MkCTA testid="hero-cta" onClick={() => navigate("/features/student-deals")}>Explore Student Deals</MkCTA>
              <button
                data-testid="hero-secondary-cta"
                onClick={() => navigate("/features/jobs")}
                className="rounded-full bg-white text-[#0A1633] border border-slate-200 px-8 py-4 font-medium text-sm sm:text-base transition-all duration-300 hover:bg-slate-50 hover:-translate-y-0.5 focus:ring-2 focus:ring-[#D4AF37] focus:outline-none"
              >
                Find Part-Time Jobs
              </button>
            </div>
            <p className="mt-5 text-sm text-slate-500">Made for student life · Save 30% or more</p>
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.2 }}
            className="mt-9 flex flex-wrap gap-2.5" data-testid="hero-benefits">
            {HERO_BENEFITS.map((b) => (
              <button
                key={b.label}
                data-testid={`hero-benefit-${b.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                onClick={() => navigate(b.link)}
                className="flex items-center gap-2 rounded-full bg-white border border-slate-200 px-4 py-2.5 text-sm font-medium text-[#0A1633] transition-all duration-300 hover:border-[#D4AF37] hover:-translate-y-0.5 focus:ring-2 focus:ring-[#D4AF37] focus:outline-none"
              >
                <b.icon className="w-4 h-4 text-[#D4AF37]" strokeWidth={1.8} />
                {b.label}
              </button>
            ))}
          </motion.div>
          </div>
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.15 }} data-testid="categories-section">
            <p className="text-sm font-semibold tracking-[0.15em] uppercase text-[#D4AF37]">Member benefits</p>
            <h2 className="mt-3 text-2xl font-bold tracking-tight text-[#0A1633]" style={{ fontFamily: "'Outfit', sans-serif" }}>Everything You Need for Student Life in the UK</h2>
            <div className="mt-6 grid grid-cols-2 gap-3">
              {CATEGORIES.map((c) => (
                <button
                  key={c.title}
                  data-testid={`category-${c.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  onClick={() => navigate(c.link)}
                  className={`text-left rounded-2xl p-4 sm:p-5 transition-all duration-300 hover:-translate-y-0.5 focus:ring-2 focus:ring-[#D4AF37] focus:outline-none ${
                    c.big ? "bg-[#0A1633] hover:shadow-[0_16px_32px_rgb(10,22,51,0.25)]" : "bg-white border border-slate-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:shadow-[0_16px_32px_rgb(0,0,0,0.08)]"
                  }`}
                >
                  <c.icon className={`w-5 h-5 ${c.big ? "text-[#D4AF37]" : "text-[#0A1633]"}`} strokeWidth={1.7} />
                  <h3 className={`mt-2.5 text-sm font-semibold leading-snug ${c.big ? "text-white" : "text-[#0A1633]"}`} style={{ fontFamily: "'Outfit', sans-serif" }}>{c.title}</h3>
                  {c.big && <p className="mt-1.5 text-xs text-slate-300 leading-relaxed">{c.copy}</p>}
                  {c.extra && <p className="mt-1.5 text-xs font-semibold text-[#D4AF37] leading-relaxed">{c.extra}</p>}
                </button>
              ))}
            </div>
          </motion.div>
        </div>
      </section>
      {/* HERO IMAGE */}
      <section className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-8 sm:py-12" data-testid="hero-image-section">
        <motion.div initial={{ opacity: 0, scale: 0.97 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }} transition={{ duration: 0.8 }} className="relative">
          <img src="https://static.prod-images.emergentagent.com/jobs/1ac370bb-7542-4338-bdfe-ff29fcb19a40/images/b3443e14ec91a0d73e137a85dd2eecd027967a65a1cf10f0dd6176a1c01be50b.jpeg" alt="Indian students laughing together on a UK university campus" className="rounded-3xl w-full object-cover aspect-[4/3] sm:aspect-[21/9] shadow-[0_20px_40px_rgb(0,0,0,0.08)]" />
          <div className="absolute -bottom-6 -left-4 sm:-left-8 bg-white border border-slate-100 shadow-[0_8px_30px_rgb(0,0,0,0.06)] rounded-2xl px-6 py-4">
            <p className="text-sm font-semibold text-[#0A1633]">Save every week.</p>
            <p className="text-xs text-slate-500 mt-0.5">Member deals & discounts</p>
          </div>
          <div className="absolute -top-5 -right-3 sm:-right-6 bg-white border border-slate-100 shadow-[0_8px_30px_rgb(0,0,0,0.06)] rounded-2xl px-6 py-4">
            <p className="text-sm font-semibold text-[#10B981]">100% free</p>
            <p className="text-xs text-slate-500 mt-0.5">Membership, always</p>
          </div>
        </motion.div>
      </section>

      {/* TRUST BAR */}
      <section className="bg-white border-b border-slate-100" data-testid="trust-bar">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-5 sm:py-7">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-4 gap-y-4 sm:gap-6">
            {TRUST_BAR.map((t, i) => (
              <motion.div key={t.label} {...fade(i * 0.05)} data-testid={`trust-badge-${i}`} className="flex items-center gap-3">
                <span className="w-9 h-9 rounded-xl bg-[#10B981]/10 flex items-center justify-center shrink-0">
                  <t.icon className="w-5 h-5 text-[#10B981]" strokeWidth={1.8} />
                </span>
                <p className="text-xs sm:text-sm font-medium text-[#0A1633] leading-snug">{t.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* WORRIES vs CASTLES */}
      <section className="border-b border-slate-100" data-testid="comparison-section">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-16 sm:py-24">
          <motion.div {...fade()}>
            <p className="text-sm font-semibold tracking-[0.15em] uppercase text-[#D4AF37]">Sound familiar?</p>
            <H className="mt-4 max-w-2xl">Everyday student problems, handled.</H>
          </motion.div>
          <div className="mt-10 sm:mt-14 space-y-5">
            {COMPARISONS.map((c, i) => (
              <motion.div key={c.concern} {...fade(i * 0.05)} data-testid={`comparison-${i}`}
                className="bg-white border border-slate-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] rounded-3xl overflow-hidden transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_20px_40px_rgb(0,0,0,0.08)]">
                <div className="grid md:grid-cols-2">
                  <div className="p-6 sm:p-8">
                    <p className="text-xs font-semibold tracking-[0.15em] uppercase text-slate-400">Before joining Castles</p>
                    <p className="mt-3 text-lg sm:text-xl font-semibold text-[#0A1633]" style={{ fontFamily: "'Outfit', sans-serif" }}>{c.concern}</p>
                  </div>
                  <div className="bg-[#0A1633] p-6 sm:p-8 border-t md:border-t-0 md:border-l border-white/10">
                    <p className="text-xs font-semibold tracking-[0.15em] uppercase text-[#D4AF37] flex items-center gap-2">
                      <c.icon className="w-4 h-4" strokeWidth={1.8} /> With Castles
                    </p>
                    <p className="mt-3 text-sm sm:text-base text-slate-300 leading-relaxed">{c.solution}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
          <motion.div {...fade(0.25)} className="mt-10 sm:mt-12">
            <MkCTA testid="comparison-cta" />
          </motion.div>
        </div>
      </section>

      {/* CASTLES AI + LIVE SUPPORT */}
      <section className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-16 sm:py-24" data-testid="castles-ai-section">
        <div className="grid lg:grid-cols-2 gap-14 items-center">
          <motion.div {...fade()}>
            <p className="text-sm font-semibold tracking-[0.15em] uppercase text-[#D4AF37]">Castles AI + Live Support</p>
            <H className="mt-4">Never feel stuck in the UK again.</H>
            <p className="mt-5 text-lg text-slate-600 leading-relaxed max-w-lg">
              University deadlines, accommodation, visas, banking, healthcare, transport, online scams, paperwork or everyday life. Whenever you're unsure, Castles is your first point of contact: ask Castles AI instantly, or request support from our team.
            </p>
            <p className="mt-4 text-lg text-[#0A1633] font-medium">You never have to figure things out alone.</p>
            <div className="mt-8 flex flex-wrap gap-2.5" data-testid="ai-topics">
              {AI_TOPICS.map((t) => (
                <span key={t} className="text-sm font-medium text-slate-600 bg-white border border-slate-200 rounded-full px-4 py-2">{t}</span>
              ))}
            </div>
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <MkCTA testid="ai-section-cta" />
              <button
                data-testid="ai-section-secondary"
                onClick={() => navigate("/features/castles-ai")}
                className="rounded-full bg-white text-[#0A1633] border border-slate-200 px-8 py-4 font-medium text-sm sm:text-base transition-all duration-300 hover:bg-slate-50 hover:-translate-y-0.5 focus:ring-2 focus:ring-[#D4AF37] focus:outline-none"
              >
                Meet Castles AI
              </button>
            </div>
          </motion.div>
          <motion.div {...fade(0.12)} className="space-y-5">
            <div className="bg-white border border-slate-100 shadow-[0_20px_40px_rgb(0,0,0,0.08)] rounded-3xl p-7" data-testid="ai-chat-preview">
              <div className="flex items-center gap-3 pb-5 border-b border-slate-100">
                <div className="w-10 h-10 rounded-xl bg-[#0A1633] flex items-center justify-center">
                  <Bot className="w-5 h-5 text-[#D4AF37]" strokeWidth={1.8} />
                </div>
                <div>
                  <p className="text-sm font-semibold text-[#0A1633]">Castles AI</p>
                  <p className="text-xs text-[#10B981] font-medium flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-[#10B981]" /> Always on for members</p>
                </div>
              </div>
              <div className="pt-5 space-y-4">
                <div className="ml-auto max-w-[85%] bg-[#0A1633] text-white rounded-2xl rounded-br-md px-5 py-3.5 text-sm">How do I register with a GP after I arrive?</div>
                <div className="max-w-[85%] bg-slate-50 border border-slate-100 text-slate-700 rounded-2xl rounded-bl-md px-5 py-3.5 text-sm leading-relaxed">It takes about 10 minutes. Find your nearest GP surgery on the NHS website, fill in a GMS1 form and show your student proof. Want me to walk you through it for your city?</div>
              </div>
            </div>
            <div className="bg-[#0A1633] rounded-3xl p-7 flex items-start gap-5" data-testid="live-support-card">
              <div className="w-11 h-11 rounded-xl bg-[#D4AF37]/15 flex items-center justify-center shrink-0">
                <Headset className="w-5 h-5 text-[#D4AF37]" strokeWidth={1.8} />
              </div>
              <div>
                <p className="text-white font-semibold">Prefer talking to a human?</p>
                <p className="mt-1.5 text-sm text-slate-300 leading-relaxed">Request live support from the Castles team. Real people, real experience, genuinely happy to help.</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* WHAT YOU UNLOCK */}
      <section className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-16 sm:py-24" data-testid="unlock-section">
        <motion.div {...fade()}>
          <p className="text-sm font-semibold tracking-[0.15em] uppercase text-[#D4AF37]">Membership, unlocked</p>
          <H className="mt-4 max-w-2xl">What you'll unlock as a member</H>
        </motion.div>
        <div className="mt-12 grid md:grid-cols-3 gap-8">
          {OUTCOMES.map((o, i) => (
            <motion.button
              key={o.slug}
              {...fade(i * 0.07)}
              data-testid={`unlock-card-${o.slug}`}
              onClick={() => navigate(`/features/${o.slug}`)}
              className="text-left bg-white border border-slate-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] rounded-3xl p-8 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_20px_40px_rgb(0,0,0,0.08)] focus:ring-2 focus:ring-[#D4AF37] focus:outline-none"
            >
              <div className="w-12 h-12 rounded-2xl flex items-center justify-center" style={{ backgroundColor: `${o.color}14` }}>
                <o.icon className="w-6 h-6" style={{ color: o.color }} strokeWidth={1.5} />
              </div>
              <h3 className="mt-6 text-xl font-semibold text-[#0A1633]" style={{ fontFamily: "'Outfit', sans-serif" }}>{o.title}</h3>
              <p className="mt-2.5 text-slate-600 leading-relaxed">{o.copy}</p>
              <p className="mt-4 text-sm font-medium flex items-center gap-1.5" style={{ color: o.color }}>
                Explore <ArrowRight className="w-4 h-4" />
              </p>
            </motion.button>
          ))}
        </div>
      </section>

      {/* SUCCESS STORIES */}
      <section className="bg-white border-y border-slate-100" data-testid="stories-section">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-24 sm:py-32">
          <motion.div {...fade()}>
            <p className="text-sm font-semibold tracking-[0.15em] uppercase text-[#D4AF37]">Student success stories</p>
            <H className="mt-4 max-w-2xl">Stories from students building their life in the UK</H>
            <p className="mt-4 text-lg text-slate-600 max-w-xl">Different cities, different courses, same result. This is student life with Castles behind you.</p>
          </motion.div>
          <div className="mt-14 grid md:grid-cols-3 gap-8">
            {STORIES.map((s, i) => (
              <motion.figure key={i} {...fade(i * 0.08)} data-testid={`story-${i}`}
                className="bg-[#FDFDFC] border border-slate-100 rounded-3xl overflow-hidden transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_20px_40px_rgb(0,0,0,0.08)]">
                <img src={s.img} alt="Castles member" className="w-full aspect-[4/3] object-cover" loading="lazy" />
                <figcaption className="p-7">
                  <blockquote className="text-slate-600 leading-relaxed">{s.quote}</blockquote>
                  <p className="mt-5 text-sm font-semibold text-[#0A1633]">{s.name}</p>
                  <p className="text-xs text-slate-500 mt-1">{s.uni}</p>
                </figcaption>
              </motion.figure>
            ))}
          </div>
        </div>
      </section>

      {/* COMMUNITY */}
      <section className="bg-white border-y border-slate-100" data-testid="community-section">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-16 sm:py-24">
          <div className="grid lg:grid-cols-2 gap-14 items-center">
            <motion.div {...fade()}>
              <p className="text-sm font-semibold tracking-[0.15em] uppercase text-[#D4AF37]">Community</p>
              <H className="mt-4">More than discounts, it's your student community.</H>
              <p className="mt-5 text-lg text-slate-600 leading-relaxed max-w-lg">
                Deals save you money. People make it home. Castles members get both: city circles, meetups, members-only events and a growing network of students who remember exactly what your first year felt like.
              </p>
              <p className="mt-4 text-lg text-[#0A1633] font-medium max-w-lg">
                A platform you'll come back to every week, not a service you use once.
              </p>
              <div className="mt-9">
                <MkCTA testid="community-join-button">Join Castles</MkCTA>
              </div>
            </motion.div>
            <motion.div {...fade(0.1)}>
              <img src="https://images.pexels.com/photos/8199169/pexels-photo-8199169.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" alt="Students connecting in the Castles community" className="rounded-3xl w-full object-cover aspect-[4/3] shadow-[0_20px_40px_rgb(0,0,0,0.08)]" loading="lazy" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* WHY TRUST */}
      <section className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-24 sm:py-32" data-testid="trust-section">
        <motion.div {...fade()}>
          <p className="text-sm font-semibold tracking-[0.15em] uppercase text-[#D4AF37]">Why students trust Castles</p>
          <H className="mt-4 max-w-2xl">Advice from people who sat exactly where you're sitting</H>
        </motion.div>
        <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {TRUST.map((t, i) => (
            <motion.div key={t.title} {...fade(i * 0.06)} data-testid={`trust-${i}`}
              className={`rounded-3xl p-8 transition-all duration-300 hover:-translate-y-1 ${i === 4 ? "bg-[#0A1633] hover:shadow-[0_20px_40px_rgb(10,22,51,0.25)]" : "bg-white border border-slate-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:shadow-[0_20px_40px_rgb(0,0,0,0.08)]"}`}>
              <t.icon className={`w-7 h-7 ${i === 4 ? "text-[#D4AF37]" : "text-[#10B981]"}`} strokeWidth={1.5} />
              <h3 className={`mt-5 text-lg font-semibold ${i === 4 ? "text-white" : "text-[#0A1633]"}`} style={{ fontFamily: "'Outfit', sans-serif" }}>{t.title}</h3>
              <p className={`mt-2.5 leading-relaxed ${i === 4 ? "text-slate-300" : "text-slate-600"}`}>{t.copy}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 pb-24 sm:pb-32" data-testid="final-cta-section">
        <motion.div {...fade()} className="relative bg-[#0A1633] rounded-[2.5rem] px-8 sm:px-16 py-16 sm:py-24 overflow-hidden">
          <div className="absolute -top-32 -right-32 w-96 h-96 rounded-full bg-[#D4AF37]/10 blur-3xl" />
          <div className="absolute -bottom-32 -left-32 w-96 h-96 rounded-full bg-[#10B981]/10 blur-3xl" />
          <div className="relative max-w-2xl">
            <h2 className="text-3xl sm:text-5xl font-bold tracking-tight text-white leading-tight" style={{ fontFamily: "'Outfit', sans-serif" }} data-testid="final-cta-headline">
              Make every week of student life count.
            </h2>
            <p className="mt-6 text-lg text-slate-300 leading-relaxed">
              Join today. <span className="text-white font-medium">Save more.</span> <span className="text-[#D4AF37] font-medium">Earn sooner.</span> <span className="text-[#10B981] font-medium">Belong faster.</span>
            </p>
            <div className="mt-10">
              <MkCTA testid="final-cta-button" className="!bg-[#D4AF37] !text-[#0A1633] hover:!shadow-[0_20px_40px_rgb(212,175,55,0.3)]">
                Become a Free Member <ArrowRight className="inline w-4 h-4 ml-1" />
              </MkCTA>
            </div>
          </div>
        </motion.div>
      </section>
    </main>
  );
}
